import { Color, Spacing } from '@persona-id/design-tokens';
import { ImageIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import Lottie from 'lottie-react';
import { ReactNode, SyntheticEvent, useCallback, useState } from 'react';
import { FileError, FileRejection, useDropzone } from 'react-dropzone';
import styled, { css } from 'styled-components';

import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import {
  Asset,
  AssetType,
  AssetUploadData,
  PersonaAsset,
  UploadedAsset,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import ProductUpsellBanner from 'dashboard/apps/InquiryTemplateEditor/ProductUpsellBanner';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { Feature } from 'dashboard/models/inquiry-template-version';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { paragraphMixins } from 'design-system/mixins/text';
import IconChevron from 'images/icons/chevron.svg';
import CreatePersonaCtaIcon from 'images/inquiry/create-persona/cta-icon.svg';
import ImageFingerprint from 'images/inquiry/create-persona/icon-fingerprint.svg';
import ImageShield from 'images/inquiry/create-persona/icon-shield.svg';
import ImageSpeed from 'images/inquiry/create-persona/icon-speed.svg';
import PasskeyRegistrationHero from 'images/inquiry/create-persona/passkey-registration-hero.json';
import CreatePersonaSuccessHero from 'images/inquiry/create-persona/success-hero.json';
import ImageCheck from 'images/inquiry/id-class/check.svg';
import ImageIdDocument from 'images/inquiry/id-class/document.svg';
import ImageFlag from 'images/inquiry/id-class/flag.svg';
import ImageGlobe from 'images/inquiry/id-class/globe.svg';
import GovernmentIdNfcScanBackClosed from 'images/inquiry/id-class/government_id_nfc_scan_back_closed.json';
import GovernmentIdNfcScanBackThenFront from 'images/inquiry/id-class/government_id_nfc_scan_back_then_front.json';
import GovernmentIdNfcScanFrontClosed from 'images/inquiry/id-class/government_id_nfc_scan_front_closed.json';
import GovernmentIdNfcScanIdCard from 'images/inquiry/id-class/government_id_nfc_scan_id_card.json';
import GovernmentIdNfcScanPhotoPage from 'images/inquiry/id-class/government_id_nfc_scan_photo_page.json';
import ImageHome from 'images/inquiry/id-class/home.svg';
import ImageIdHorizontal from 'images/inquiry/id-class/id-horizontal.svg';
import ImagePhone from 'images/inquiry/id-class/phone.svg';
import ImageUser from 'images/inquiry/id-class/user.svg';
import StepperCompletedIcon from 'images/inquiry/stepper/completed.svg';
import StepperConnectorIcon from 'images/inquiry/stepper/connector.svg';
import StepperCurrentIcon from 'images/inquiry/stepper/current.svg';
import StepperErroredIcon from 'images/inquiry/stepper/errored.svg';
import StepperIncompleteIcon from 'images/inquiry/stepper/incomplete.svg';
import VerifyWithPersonaAddressIcon from 'images/inquiry/verify-with-persona/address-icon.svg';
import VerifyWithPersonaIdIcon from 'images/inquiry/verify-with-persona/id-icon.svg';
import VerifyWithPersonaIndividualAttributesIcon from 'images/inquiry/verify-with-persona/individual-attributes-icon.svg';
import VerifyWithPersonaNameIcon from 'images/inquiry/verify-with-persona/name-icon.svg';
import PasskeyAuthenticationHero from 'images/inquiry/verify-with-persona/passkey-authentication-hero.json';
import PersonaLogo from 'images/inquiry/verify-with-persona/persona-logo.svg';
import VerifyWithPersonaSelfieIcon from 'images/inquiry/verify-with-persona/selfie-icon.svg';
import ImageDefaultFailed from 'images/pictographs/default-failed.svg';
import ImageDocument from 'images/pictographs/document.svg';
import ImageDocumentFailed from 'images/pictographs/document-failed.svg';
import ImageIdBackFail from 'images/pictographs/id-back-fail.svg';
import ImageFailed from 'images/pictographs/id-failed.svg';
import ImageIdFrontFail from 'images/pictographs/id-front-fail.svg';
import ImageSelfieFail from 'images/pictographs/selfie-fail.svg';
import StartHero from 'images/pictographs/start-hero.svg';
import CachedImage from 'inquiry/components/ImageRemote/CachedImage';
import VerificationHero from 'inquiry/components/VerificationHero';
import { readFile } from 'lib/file';
import { ImageKey } from 'lib/interfaces/ui-components';
import { dataUrlToBase64 } from 'lib/utils/dataUrl';
import { assertNever } from 'lib/utils/types';
import AnimatedCheck from 'verify/components/AnimatedCheck';

enum FileErrorCode {
  FileTooLarge = 'file-too-large',
  TooManyFiles = 'too-many-files',
  FileInvalidType = 'file-invalid-type',
}

const ITEM_SIZE = 136;
const GALLERY_HEIGHT = 350;
const ACCEPTED_IMAGE_FORMATS = [
  'image/png',
  'image/gif',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
  'application/json',
];
const MAX_NUMBER_OF_FILES = 10;
const MAX_FILE_SIZE = 1048576;

const HOVER_FOCUS = css`
  cursor: pointer;
  &:focus {
    box-shadow: 0 0 0 3px ${({ theme }) => theme.color.border.secondary};
  }
  &:hover {
    box-shadow: 0 0 0 3px ${({ theme }) => theme.color.border.secondary};
  }
`;

const ITEM_STYLE = css`
  height: ${ITEM_SIZE}px;
  width: ${ITEM_SIZE}px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color.border.secondary};
  ${HOVER_FOCUS}
`;

const PreviewImageContainer = styled.figure`
  ${ITEM_STYLE}
  background-color: ${({ theme }) => theme.color.background.secondary};
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ErrorMessage = styled('div')`
  color: ${({ theme }) => theme.color.variants.danger.default};
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
`;

export const PreviewImageInner = styled.div`
  padding: ${Spacing[3]};
  flex: auto;
  /* Stop flex child from growing out of parent */
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const AspectRatioGuard = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Thumbnail = styled(CachedImage)`
  height: auto;
  width: auto;
  max-height: 100%;
  max-width: 100%;
  image-orientation: from-image;
`;

export const Caption = styled.figcaption`
  height: 32px;
  width: 100%;
  flex: none;
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.color.background.primary};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

export const CaptionText = styled.span`
  ${paragraphMixins.sm}
  color: ${({ theme }) => theme.color.text.secondary};
  padding: ${Spacing[2]} ${Spacing[3]};
  overflow-x: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const WrapperButton = styled.button`
  width: ${ITEM_SIZE}px;
  height: ${ITEM_SIZE}px;
  border-radius: 8px;

  ${({ disabled }) =>
    disabled &&
    css`
      figure {
        cursor: not-allowed;
        filter: opacity(0.5);
        &:focus {
          box-shadow: none;
        }
        &:hover {
          box-shadow: none;
        }
      }
    `}
`;

const AnimatedCheckContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  // this actually results in net zero margin because the animated check has
  // negative margin for some reason.
  margin-top: ${Spacing[6]};
  margin-bottom: ${Spacing[6]};
  & > div {
    width: 80px;
    height: 80px;
  }
`;

const StyledVerificationHero = styled(VerificationHero)`
  height: 100%;
`;

const personaAssets = Object.values(ImageKey)
  .map((imageKey) => {
    switch (imageKey) {
      case ImageKey.AnimatedCheck:
      case ImageKey.FailedIcon:
      case ImageKey.StartHero:
      case ImageKey.FailureDefaultIcon:
      case ImageKey.FailureDocumentIcon:
      case ImageKey.FailureSelfieIcon:
      case ImageKey.IdBackIcon:
      case ImageKey.IdFrontIcon:
      case ImageKey.DocumentStartHero:
      case ImageKey.OptionFlagIcon:
      case ImageKey.OptionGlobeIcon:
      case ImageKey.OptionHomeIcon:
      case ImageKey.OptionIdIcon:
      case ImageKey.OptionIdDocumentIcon:
      case ImageKey.OptionUserIcon:
      case ImageKey.OptionPhoneIcon:
      case ImageKey.OptionCheckIcon:
      case ImageKey.OptionChevronIcon:
        return {
          type: AssetType.PERSONA,
          imageKey,
        } as PersonaAsset;

      case ImageKey.CreatePersonaCtaCardIcon:
      case ImageKey.CreatePersonaPasskeyRegistrationHero:
      case ImageKey.CreatePersonaPasskeyRegistrationSpeedIcon:
      case ImageKey.CreatePersonaPasskeyRegistrationShieldIcon:
      case ImageKey.CreatePersonaPasskeyRegistrationFingerprintIcon:
      case ImageKey.CreatePersonaSuccessHero:
      case ImageKey.VerifyWithPersonaPasskeyAuthenticationHero:
      case ImageKey.VerifyWithPersonaPasskeyAuthenticationAddressIcon:
      case ImageKey.VerifyWithPersonaPasskeyAuthenticationIdIcon:
      case ImageKey.VerifyWithPersonaPasskeyAuthenticationIndividualAttributesIcon:
      case ImageKey.VerifyWithPersonaPasskeyAuthenticationNameIcon:
      case ImageKey.VerifyWithPersonaPasskeyAuthenticationPersonaLogo:
      case ImageKey.VerifyWithPersonaPasskeyAuthenticationSelfieIcon:
      case ImageKey.GovernmentIdNfcScanBackClosed:
      case ImageKey.GovernmentIdNfcScanFrontClosed:
      case ImageKey.GovernmentIdNfcScanPhotoPage:
      case ImageKey.GovernmentIdNfcScanIdCard:
      case ImageKey.GovernmentIdNfcScanBackThenFront:
      case ImageKey.StepperIncompleteIcon:
      case ImageKey.StepperCurrentIcon:
      case ImageKey.StepperErroredIcon:
      case ImageKey.StepperCompletedIcon:
      case ImageKey.StepperConnectorIcon:
        return null;

      default:
        assertNever(imageKey);
    }
  })
  .filter(Boolean);

export const renderPersonaAsset = (imageKey: ImageKey) => {
  switch (imageKey) {
    case ImageKey.AnimatedCheck:
      return (
        <AnimatedCheckContainer>
          <AnimatedCheck />
        </AnimatedCheckContainer>
      );
    case ImageKey.FailedIcon:
      return <ImageFailed style={{ width: '70px', height: '60px' }} />;
    case ImageKey.StartHero:
      return <StartHero style={{ width: '90px', height: '66px' }} />;
    case ImageKey.FailureDefaultIcon:
      return <ImageDefaultFailed style={{ width: '50px', height: '50px' }} />;
    case ImageKey.FailureDocumentIcon:
      return <ImageDocumentFailed style={{ width: '75px', height: '60px' }} />;
    case ImageKey.FailureSelfieIcon:
      return <ImageSelfieFail style={{ width: '52px', height: '56px' }} />;
    case ImageKey.IdBackIcon:
      return <ImageIdBackFail style={{ width: '75px', height: '50px' }} />;
    case ImageKey.IdFrontIcon:
      return <ImageIdFrontFail style={{ width: '75px', height: '50px' }} />;
    case ImageKey.DocumentStartHero:
      return (
        <StyledVerificationHero backgroundColor={Color.CorePurple200}>
          <ImageDocument height={50} width='100%' />
        </StyledVerificationHero>
      );
    case ImageKey.OptionFlagIcon:
      return <ImageFlag />;
    case ImageKey.OptionGlobeIcon:
      return <ImageGlobe />;
    case ImageKey.OptionHomeIcon:
      return <ImageHome />;
    case ImageKey.OptionIdIcon:
      return <ImageIdHorizontal />;
    case ImageKey.OptionPhoneIcon:
      return <ImagePhone />;
    case ImageKey.OptionIdDocumentIcon:
      return <ImageIdDocument />;
    case ImageKey.OptionUserIcon:
      return <ImageUser />;
    case ImageKey.OptionCheckIcon:
      return <ImageCheck />;
    case ImageKey.OptionChevronIcon:
      return <IconChevron />;

    // not available in asset management modal directly,
    // but define rendering here for other places that use this function
    case ImageKey.CreatePersonaCtaCardIcon:
      return <CreatePersonaCtaIcon />;
    case ImageKey.CreatePersonaPasskeyRegistrationHero:
      return <Lottie animationData={PasskeyRegistrationHero} />;
    case ImageKey.CreatePersonaPasskeyRegistrationSpeedIcon:
      return <ImageSpeed />; // Ka chow!
    case ImageKey.CreatePersonaPasskeyRegistrationShieldIcon:
      return <ImageShield />;
    case ImageKey.CreatePersonaPasskeyRegistrationFingerprintIcon:
      return <ImageFingerprint />;
    case ImageKey.CreatePersonaSuccessHero:
      return <Lottie animationData={CreatePersonaSuccessHero} />;
    case ImageKey.VerifyWithPersonaPasskeyAuthenticationHero:
      return <Lottie animationData={PasskeyAuthenticationHero} />;
    case ImageKey.VerifyWithPersonaPasskeyAuthenticationAddressIcon:
      return <VerifyWithPersonaAddressIcon />;
    case ImageKey.VerifyWithPersonaPasskeyAuthenticationIdIcon:
      return <VerifyWithPersonaIdIcon />;
    case ImageKey.VerifyWithPersonaPasskeyAuthenticationIndividualAttributesIcon:
      return <VerifyWithPersonaIndividualAttributesIcon />;
    case ImageKey.VerifyWithPersonaPasskeyAuthenticationNameIcon:
      return <VerifyWithPersonaNameIcon />;
    case ImageKey.VerifyWithPersonaPasskeyAuthenticationPersonaLogo:
      return <PersonaLogo />;
    case ImageKey.VerifyWithPersonaPasskeyAuthenticationSelfieIcon:
      return <VerifyWithPersonaSelfieIcon />;
    case ImageKey.GovernmentIdNfcScanBackClosed:
      return <Lottie animationData={GovernmentIdNfcScanBackClosed} />;
    case ImageKey.GovernmentIdNfcScanFrontClosed:
      return <Lottie animationData={GovernmentIdNfcScanFrontClosed} />;
    case ImageKey.GovernmentIdNfcScanPhotoPage:
      return <Lottie animationData={GovernmentIdNfcScanPhotoPage} />;
    case ImageKey.GovernmentIdNfcScanIdCard:
      return <Lottie animationData={GovernmentIdNfcScanIdCard} />;
    case ImageKey.GovernmentIdNfcScanBackThenFront:
      return <Lottie animationData={GovernmentIdNfcScanBackThenFront} />;
    case ImageKey.StepperIncompleteIcon:
      return <StepperIncompleteIcon />;
    case ImageKey.StepperCurrentIcon:
      return <StepperCurrentIcon />;
    case ImageKey.StepperCompletedIcon:
      return <StepperCompletedIcon />;
    case ImageKey.StepperErroredIcon:
      return <StepperErroredIcon />;
    case ImageKey.StepperConnectorIcon:
      return <StepperConnectorIcon />;
    default:
      assertNever(imageKey);
  }
};

interface GalleryEntryProps {
  id: string;
  asset: Asset;
  filename: string;
  children: ReactNode;
  disabled: boolean;
  handleClose: () => void;
  handleImageSelect: (asset: Asset) => void;
}

const GalleryEntry = ({
  id,
  asset,
  filename,
  children,
  disabled,
  handleClose,
  handleImageSelect,
}: GalleryEntryProps) => {
  return (
    <WrapperButton
      aria-label={filename}
      id={id}
      onClick={() => {
        handleImageSelect(asset);
        handleClose();
      }}
      disabled={disabled}
    >
      <PreviewImageContainer>
        <PreviewImageInner>
          <AspectRatioGuard>{children}</AspectRatioGuard>
        </PreviewImageInner>
        <Caption title={filename}>
          <CaptionText>{filename}</CaptionText>
        </Caption>
      </PreviewImageContainer>
    </WrapperButton>
  );
};

const RootContainer = styled.div<{ hasErrors: boolean }>`
  padding-bottom: ${({ hasErrors }) => (hasErrors ? Spacing[2] : Spacing[0])};
`;

const MainContentContainer = styled.div<{ hasErrors: boolean }>`
  ${paragraphMixins.md};
  /* No right padding to handle scroll bar */
  padding: 0;
  overflow: hidden;
  width: 445px;
`;

const GalleryButton = styled.button`
  ${ITEM_STYLE}
`;

const Gallery = styled.div`
  display: grid;
  max-height: ${GALLERY_HEIGHT}px;
  grid-template-columns: repeat(3, ${ITEM_SIZE}px);
  gap: ${Spacing[4]};
  margin-top: ${Spacing[4]};
  padding: 3px 0 3px 3px;
  overflow: auto;
  padding-right: ${Spacing[5]};
  margin-right: -${Spacing[4]};
`;

const DropGraphic = styled.div`
  height: ${GALLERY_HEIGHT}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-right: ${Spacing[7]};
  margin-bottom: ${Spacing[4]};
  border-radius: ${Spacing[2]};
  border: 1px dashed ${({ theme }) => theme.color.border.active};
  margin-top: ${Spacing[4]};
  margin-left: ${Spacing[7]};
  background-color: ${({ theme }) => theme.color.primary.brand100};
  svg {
    width: 46px;
    height: 46px;
    color: ${({ theme }) => theme.color.primary.brand700};
  }
`;

const StackDiv = styled.div`
  text-align: center;
`;

const LoadingWrapper = styled.div`
  ${ITEM_STYLE}
  padding: 38px;
`;

const SizeBoundingDiv = styled.div`
  width: 60px;
  height: 60px;
`;

const StyledLoadingIndicator = styled(LoadingIndicator)`
  padding: 0;
  height: 100%;
`;

const StyledTabBar = styled(TabBar)`
  border-bottom: none;
`;

const TabContainer = styled.div`
  height: ${GALLERY_HEIGHT}px;
`;

const getErrorMessage = (error: FileError, filename: string) => {
  const errorCode = error.code as FileErrorCode;
  switch (errorCode) {
    case FileErrorCode.FileInvalidType:
      return 'File type not supported (JPG, PNG, WEBP, GIF, SVG only).';
    case FileErrorCode.FileTooLarge:
      return `${filename} is larger than 1MB.`;
    case FileErrorCode.TooManyFiles:
      return `Maximum number of files allowed in a single upload is ${MAX_NUMBER_OF_FILES}.`;
    default:
      assertNever(errorCode);
  }
};

interface AssetManagementModalProps {
  assets: Asset[];
  isLoading: boolean;
  remoteAssetsDisabled?: boolean;
  personaAssetsDisabled?: boolean;
  handleClose: () => void;
  handleFileUpload: (files: AssetUploadData[]) => void;
  handleImageSelect: (asset: Asset) => void;
}

export const convertAssetKeyToDisplayName = (assetKey: string): string => {
  return assetKey
    .replace(/(_)/g, ' ')
    .replace(/\bid/g, 'ID')
    .replace(/(^[a-zA-Z])/g, (_, firstChar: string) => {
      const capitalizedFirst = firstChar != null ? firstChar.toUpperCase() : '';
      return capitalizedFirst;
    });
};

export const AssetManagementModal = ({
  assets,
  isLoading,
  remoteAssetsDisabled,
  personaAssetsDisabled,
  handleClose,
  handleFileUpload,
  handleImageSelect,
}: AssetManagementModalProps) => {
  const [numberUploading, setNumberUploading] = useState<number>(0);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejections: FileRejection[]) => {
      // handle accepted files
      setNumberUploading(acceptedFiles.length);
      const filesToUpload = acceptedFiles.map(async (file: File) => ({
        'asset-data': dataUrlToBase64((await readFile(file)) as string),
        'asset-filename': file.name,
      }));
      Promise.all(filesToUpload).then((result) => handleFileUpload(result));
      // handle rejected files
      setErrorMessages([
        ...new Set(
          rejections.flatMap((rejection) =>
            rejection.errors.map((err) => getErrorMessage(err, rejection.file.name)),
          ),
        ),
      ]);
    },
    [handleFileUpload],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: ACCEPTED_IMAGE_FORMATS,
    noClick: true,
    maxFiles: MAX_NUMBER_OF_FILES,
    maxSize: MAX_FILE_SIZE,
  });

  const BannerWrapper = styled.div`
    margin: ${Spacing[2]} 0;
  `;

  const tabs = [
    <TabContainer key='my-assets-tab' {...getRootProps()}>
      <input {...getInputProps()} />
      {isDragActive ? (
        <DropGraphic key='drop-graphic'>
          <StackDiv>
            <ImageIcon />
            <p>Drop your file to upload</p>
          </StackDiv>
        </DropGraphic>
      ) : (
        <>
          {remoteAssetsDisabled && (
            <BannerWrapper>
              <ProductUpsellBanner
                feature={Feature.Asset}
                learnMoreId='custom-assets-upsell'
                accessTier={EditorAccessTier.InquiriesEnhanced}
                message='Custom assets can be added only to the start screen under your current plan. To learn more about other plans, you can send a message to a Persona representative.'
              />
            </BannerWrapper>
          )}

          <Gallery>
            <GalleryButton
              aria-label='Add'
              disabled={remoteAssetsDisabled}
              id='asset-management-modal__add-new-asset'
              onClick={(e: SyntheticEvent<HTMLButtonElement>) => {
                // stop propagation to prevent double-fire of dialog
                e.preventDefault();
                e.stopPropagation();
                open();
              }}
            >
              <StackDiv>
                <PlusIcon />
                <p>Add new</p>
              </StackDiv>
            </GalleryButton>
            {isLoading && (
              <>
                {[...Array(numberUploading)].map((_, idx) => (
                  <LoadingWrapper key={`loading-spinner-${idx}`}>
                    <SizeBoundingDiv>
                      <StyledLoadingIndicator />
                    </SizeBoundingDiv>
                  </LoadingWrapper>
                ))}
              </>
            )}
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            {assets.map((asset: UploadedAsset) => (
              <GalleryEntry
                key={asset['blob-id']}
                asset={asset}
                handleImageSelect={handleImageSelect}
                handleClose={handleClose}
                filename={asset.filename}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                disabled={remoteAssetsDisabled}
                id={asset['blob-id']}
              >
                <Thumbnail src={asset.url} contentType={asset['content-type']} />
              </GalleryEntry>
            ))}
          </Gallery>
        </>
      )}
    </TabContainer>,
    <TabContainer key='persona-assets-tab'>
      <Gallery>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        {personaAssets.map((asset: PersonaAsset) => (
          <GalleryEntry
            key={asset.imageKey}
            asset={asset}
            handleImageSelect={handleImageSelect}
            handleClose={handleClose}
            filename={convertAssetKeyToDisplayName(asset.imageKey)}
            disabled={false}
            id={asset.imageKey}
          >
            {renderPersonaAsset(asset.imageKey)}
          </GalleryEntry>
        ))}
      </Gallery>
    </TabContainer>,
  ];

  const [selected, setSelected] = useState(0);

  const hasErrors = errorMessages.length > 0;

  return (
    <RootContainer hasErrors={hasErrors} role='dialog'>
      <MainContentContainer hasErrors={hasErrors}>
        <StyledTabBar>
          <Tab selected={selected === 0} onClick={() => setSelected(0)}>
            My assets
          </Tab>
          {!personaAssetsDisabled && (
            <Tab selected={selected === 1} onClick={() => setSelected(1)}>
              Persona assets
            </Tab>
          )}
        </StyledTabBar>
        {tabs[selected]}
      </MainContentContainer>
      {errorMessages &&
        errorMessages.map((msg, idx) => <ErrorMessage key={`error-${idx}`}>{msg}</ErrorMessage>)}
    </RootContainer>
  );
};
